import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  BarChart3, RefreshCw, DollarSign, Loader2, AlertTriangle,
  TrendingUp, Building2, Download
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const APAgingReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agingData, setAgingData] = useState([]);
  
  // Summary totals
  const [totals, setTotals] = useState({
    current: 0,
    days_1_30: 0,
    days_31_60: 0,
    days_61_90: 0,
    over_90: 0,
    total_open: 0
  });


  const loadAgingReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/ap/ap-aging`);
      setAgingData(response.data);
      calculateTotals(response.data);
    } catch (error) {
      console.error('Error loading AP aging report:', error);
      toast.error('Failed to load AP aging report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgingReport();
  }, [loadAgingReport]);

  const calculateTotals = (data) => {
    const newTotals = {
      current: 0,
      days_1_30: 0,
      days_31_60: 0,
      days_61_90: 0,
      over_90: 0,
      total_open: 0
    };

    data.forEach(item => {
      newTotals.current += item.current;
      newTotals.days_1_30 += item.days_1_30;
      newTotals.days_31_60 += item.days_31_60;
      newTotals.days_61_90 += item.days_61_90;
      newTotals.over_90 += item.over_90;
      newTotals.total_open += item.total_open;
    });

    setTotals(newTotals);
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getPercentage = (amount) => {
    if (totals.total_open === 0) return 0;
    return ((amount / totals.total_open) * 100).toFixed(1);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="ap-aging-report">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-orange-600" />
              AP Aging Report
            </h1>
            <p className="text-sm text-gray-500">Accounts Payable aging analysis by vendor</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/ap/open-items')}>
              <Building2 className="w-4 h-4 mr-2" />
              View Open Items
            </Button>
            <Button variant="outline" onClick={loadAgingReport}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 p-4">
            <p className="text-xs text-green-700">Current (Not Due)</p>
            <p className="text-xl font-bold text-green-900">{formatCurrency(totals.current)}</p>
            <p className="text-xs text-green-600">{getPercentage(totals.current)}%</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg shadow-sm border border-yellow-200 p-4">
            <p className="text-xs text-yellow-700">1-30 Days</p>
            <p className="text-xl font-bold text-yellow-900">{formatCurrency(totals.days_1_30)}</p>
            <p className="text-xs text-yellow-600">{getPercentage(totals.days_1_30)}%</p>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow-sm border border-orange-200 p-4">
            <p className="text-xs text-orange-700">31-60 Days</p>
            <p className="text-xl font-bold text-orange-900">{formatCurrency(totals.days_31_60)}</p>
            <p className="text-xs text-orange-600">{getPercentage(totals.days_31_60)}%</p>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg shadow-sm border border-red-200 p-4">
            <p className="text-xs text-red-700">61-90 Days</p>
            <p className="text-xl font-bold text-red-900">{formatCurrency(totals.days_61_90)}</p>
            <p className="text-xs text-red-600">{getPercentage(totals.days_61_90)}%</p>
          </div>
          <div className="bg-gradient-to-br from-red-100 to-red-200 rounded-lg shadow-sm border border-red-300 p-4">
            <p className="text-xs text-red-800">Over 90 Days</p>
            <p className="text-xl font-bold text-red-900">{formatCurrency(totals.over_90)}</p>
            <p className="text-xs text-red-700">{getPercentage(totals.over_90)}%</p>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow-sm border border-orange-200 p-4">
            <p className="text-xs text-orange-700">Total Open</p>
            <p className="text-xl font-bold text-orange-900">{formatCurrency(totals.total_open)}</p>
            <p className="text-xs text-orange-600">{agingData.length} vendors</p>
          </div>
        </div>

        {/* Aging Bar Visualization */}
        {totals.total_open > 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Aging Distribution</h3>
            <div className="h-8 flex rounded-lg overflow-hidden">
              {totals.current > 0 && (
                <div 
                  className="bg-green-500 flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${getPercentage(totals.current)}%` }}
                >
                  {getPercentage(totals.current) > 10 && `${getPercentage(totals.current)}%`}
                </div>
              )}
              {totals.days_1_30 > 0 && (
                <div 
                  className="bg-yellow-500 flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${getPercentage(totals.days_1_30)}%` }}
                >
                  {getPercentage(totals.days_1_30) > 10 && `${getPercentage(totals.days_1_30)}%`}
                </div>
              )}
              {totals.days_31_60 > 0 && (
                <div 
                  className="bg-orange-500 flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${getPercentage(totals.days_31_60)}%` }}
                >
                  {getPercentage(totals.days_31_60) > 10 && `${getPercentage(totals.days_31_60)}%`}
                </div>
              )}
              {totals.days_61_90 > 0 && (
                <div 
                  className="bg-red-400 flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${getPercentage(totals.days_61_90)}%` }}
                >
                  {getPercentage(totals.days_61_90) > 10 && `${getPercentage(totals.days_61_90)}%`}
                </div>
              )}
              {totals.over_90 > 0 && (
                <div 
                  className="bg-red-600 flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${getPercentage(totals.over_90)}%` }}
                >
                  {getPercentage(totals.over_90) > 10 && `${getPercentage(totals.over_90)}%`}
                </div>
              )}
            </div>
            <div className="flex gap-4 mt-2 text-xs text-gray-600">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> Current</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500 rounded"></span> 1-30 Days</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-500 rounded"></span> 31-60 Days</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded"></span> 61-90 Days</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-600 rounded"></span> Over 90 Days</span>
            </div>
          </div>
        )}

        {/* Results Table */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading aging report...</p>
            </div>
          ) : agingData.length === 0 ? (
            <div className="p-8 text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No accounts payable data found</p>
              <p className="text-sm text-gray-500 mt-2">Sync vendor open items from posted invoices</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      <span className="text-green-600">Current</span>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      <span className="text-yellow-600">1-30 Days</span>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      <span className="text-orange-600">31-60 Days</span>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      <span className="text-red-500">61-90 Days</span>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      <span className="text-red-700">Over 90</span>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      <span className="text-orange-700">Total Open</span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Invoices</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {agingData.map((vendor) => (
                    <tr key={vendor.vendor_id} className="hover:bg-gray-50" data-testid={`ap-row-${vendor.vendor_id}`}>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => drillTo(vendor.vendor_id)}>
                        <p className="font-medium text-orange-700 hover:underline">{vendor.vendor_id}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]">{vendor.vendor_name}</p>
                      </td>
                      <td className="px-4 py-3 text-right cursor-pointer hover:bg-green-50" onClick={() => drillTo(vendor.vendor_id, 'current')}>
                        <span className={vendor.current > 0 ? 'font-medium text-green-700' : 'text-gray-400'}>
                          {vendor.current > 0 ? formatCurrency(vendor.current) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right cursor-pointer hover:bg-yellow-50" onClick={() => drillTo(vendor.vendor_id, 'days_1_30')}>
                        <span className={vendor.days_1_30 > 0 ? 'font-medium text-yellow-700' : 'text-gray-400'}>
                          {vendor.days_1_30 > 0 ? formatCurrency(vendor.days_1_30) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right cursor-pointer hover:bg-orange-50" onClick={() => drillTo(vendor.vendor_id, 'days_31_60')}>
                        <span className={vendor.days_31_60 > 0 ? 'font-medium text-orange-700' : 'text-gray-400'}>
                          {vendor.days_31_60 > 0 ? formatCurrency(vendor.days_31_60) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right cursor-pointer hover:bg-red-50" onClick={() => drillTo(vendor.vendor_id, 'days_61_90')}>
                        <span className={vendor.days_61_90 > 0 ? 'font-medium text-red-600' : 'text-gray-400'}>
                          {vendor.days_61_90 > 0 ? formatCurrency(vendor.days_61_90) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right cursor-pointer hover:bg-red-100" onClick={() => drillTo(vendor.vendor_id, 'days_over_90')}>
                        <span className={vendor.over_90 > 0 ? 'font-bold text-red-700' : 'text-gray-400'}>
                          {vendor.over_90 > 0 ? formatCurrency(vendor.over_90) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-orange-700 cursor-pointer hover:bg-orange-50" onClick={() => drillTo(vendor.vendor_id)}>
                        {formatCurrency(vendor.total_open)}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600">
                        {vendor.invoice_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                  <tr className="font-bold">
                    <td className="px-4 py-3 text-gray-900">TOTAL</td>
                    <td className="px-4 py-3 text-right text-green-700">{formatCurrency(totals.current)}</td>
                    <td className="px-4 py-3 text-right text-yellow-700">{formatCurrency(totals.days_1_30)}</td>
                    <td className="px-4 py-3 text-right text-orange-700">{formatCurrency(totals.days_31_60)}</td>
                    <td className="px-4 py-3 text-right text-red-600">{formatCurrency(totals.days_61_90)}</td>
                    <td className="px-4 py-3 text-right text-red-700">{formatCurrency(totals.over_90)}</td>
                    <td className="px-4 py-3 text-right text-orange-700">{formatCurrency(totals.total_open)}</td>
                    <td className="px-4 py-3 text-center text-gray-900">
                      {agingData.reduce((sum, v) => sum + v.invoice_count, 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default APAgingReport;
